import React, { useState } from 'react';
import { Card, CardContent, Button } from '../components/ui';
import { ThatsUpWidget, AdeprimoWidget, StrawberryWidget } from '../components/widgets';
import adeprimoLogo from '../assets/images/adeprimo.png';
import thatsupLogo from '../assets/images/thatsup.svg';
import strawberryLogo from '../assets/images/strawberry.avif';

interface WidgetStyles {
  // Chat widget colors
  chatBackgroundColor: string;
  chatBorderColor: string;
  chatBorderRadius: number;
  chatWidth: number;
  chatHeight: number;
  
  // Header colors
  headerBackgroundColor: string;
  headerColor: string;
  
  // Bot message colors
  botBackgroundColor: string;
  botColor: string;
  botAvatarBackgroundColor: string;
  botAvatarColor: string;
  botAvatarSize: number;
  
  // User message colors
  userBackgroundColor: string;
  userColor: string;
  
  // Send button colors
  sendButtonBackgroundColor: string;
  sendButtonColor: string;
  
  // Input area colors
  inputAreaBackgroundColor?: string;
  
  // Toggle button colors
  toggleBackgroundColor: string;
  toggleBackgroundColorActive: string;
  toggleBorderColor: string;
  toggleBorderColorActive: string;
  toggleSize: number;
  toggleBorderRadius: number;
  
  // Positioning
  position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  bottom: number;
  right: number;
  left: number;
  top: number;
}

interface WidgetText {
  headerTitle: string;
  botMessage: string;
  inputPlaceholder: string;
  sendButtonText: string;
  emailInfoText: string;
}

type WidgetType = 'thatsup' | 'adeprimo' | 'strawberry';

export function WidgetCustomizer() {
  const [styles, setStyles] = useState<WidgetStyles>({
    // Chat widget colors
    chatBackgroundColor: '#FFFFFF',
    chatBorderColor: '#ccc',
    chatBorderRadius: 0,
    chatWidth: 375,
    chatHeight: 650,
    
    // Header colors
    headerBackgroundColor: '#1e3a34',
    headerColor: '#FFFFFF',
    
    // Bot message colors
    botBackgroundColor: '#1e3a34',
    botColor: '#FFFFFF',
    botAvatarBackgroundColor: '#1e3a34',
    botAvatarColor: '#FFFFFF',
    botAvatarSize: 40,
    
    // User message colors
    userBackgroundColor: '#f0f0f0',
    userColor: '#000000',
    
    // Send button colors
    sendButtonBackgroundColor: '#1e3a34',
    sendButtonColor: '#777777',
    
    // Input area colors
    inputAreaBackgroundColor: '#f8f9fa',
    
    // Toggle button colors
    toggleBackgroundColor: '#1e3a34',
    toggleBackgroundColorActive: '#0F1D2B',
    toggleBorderColor: '#FFFFFF',
    toggleBorderColorActive: '#FFFFFF',
    toggleSize: 60,
    toggleBorderRadius: 50,
    
    // Positioning
    position: 'bottom-right',
    bottom: 0,
    right: 0,
    left: 0,
    top: 0,
  });

  const [textContent, setTextContent] = useState<WidgetText>({
    headerTitle: 'Vivi',
    botMessage: 'Hi! How can I help you?',
    inputPlaceholder: 'Write...',
    sendButtonText: 'Send',
    emailInfoText: 'Send an e-mail to info@villacph.com if you don\'t get an answer to your question.'
  });

  const [widgetType, setWidgetType] = useState<WidgetType | null>(null);

  const updateStyle = (key: keyof WidgetStyles, value: string | number) => {
    setStyles(prev => ({ ...prev, [key]: value }));
  };

  const updateText = (key: keyof WidgetText, value: string) => {
    setTextContent(prev => ({ ...prev, [key]: value }));
  };

  const selectWidgetType = (type: WidgetType) => {
    setWidgetType(type);
    
    // Set default values based on widget type
    if (type === 'adeprimo') {
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 375,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#212450',
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#f8f9fa',
        botColor: '#222',
        botAvatarBackgroundColor: '#212450',
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#ffe6f0',
        userColor: '#000000',
        
        // Send button colors
        sendButtonBackgroundColor: '#1e3a34',
        sendButtonColor: '#777777',
        
        // Toggle button colors
        toggleBackgroundColor: '#1e3a34',
        toggleBackgroundColorActive: '#0F1D2B',
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Ask Gustav',
        botMessage: 'Hello, I\'m Gustav. How can I help you today?',
        inputPlaceholder: 'Write...',
        sendButtonText: 'Send',
        emailInfoText: 'Hi, I\'m Gustav! I\'m still learning, so if I missed your question, please reach out to info@amerikalinjen.com'
      });
    } else if (type === 'thatsup') {
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 375,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#1e3a34',
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#1e3a34',
        botColor: '#FFFFFF',
        botAvatarBackgroundColor: '#1e3a34',
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#f0f0f0',
        userColor: '#000000',
        
        // Send button colors
        sendButtonBackgroundColor: '#1e3a34',
        sendButtonColor: '#FFFFFF',
        
        // Toggle button colors
        toggleBackgroundColor: '#1e3a34',
        toggleBackgroundColorActive: '#0F1D2B',
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Vivi',
        botMessage: 'Hi! How can I help you?',
        inputPlaceholder: 'Write...',
        sendButtonText: 'Send',
        emailInfoText: 'Send an e-mail to info@villacph.com if you don\'t get an answer to your question.'
      });
    } else if (type === 'strawberry') {
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 420,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#fc5e58', // Brand Coral
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#fef7f7', // Primary 50
        botColor: '#403d3c', // Neutral 900
        botAvatarBackgroundColor: '#fc5e58', // Brand Coral
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#ffd7d7', // Secondary 50
        userColor: '#403d3c', // Neutral 900
        
        // Send button colors
        sendButtonBackgroundColor: '#fc5e58', // Brand Coral
        sendButtonColor: '#FFFFFF',
        
        // Input area colors
        inputAreaBackgroundColor: '#fafafa', // Neutral 50
        
        // Toggle button colors
        toggleBackgroundColor: '#fc5e58', // Brand Coral
        toggleBackgroundColorActive: '#e53e3a', // Primary 600
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Strawberry',
        botMessage: 'Hi! How can I help you today?',
        inputPlaceholder: 'Write your message...',
        sendButtonText: 'Send',
        emailInfoText: 'Need help? Contact us at support@strawberry.com'
      });
    }
  };

  const resetToDefault = () => {
    if (widgetType === 'adeprimo') {
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 375,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#212450',
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#f8f9fa',
        botColor: '#222',
        botAvatarBackgroundColor: '#212450',
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#ffe6f0',
        userColor: '#000000',
        
        // Send button colors
        sendButtonBackgroundColor: '#1e3a34',
        sendButtonColor: '#777777',
        
        // Toggle button colors
        toggleBackgroundColor: '#1e3a34',
        toggleBackgroundColorActive: '#0F1D2B',
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Ask Gustav',
        botMessage: 'Hello, I\'m Gustav. How can I help you today?',
        inputPlaceholder: 'Write...',
        sendButtonText: 'Send',
        emailInfoText: 'Hi, I\'m Gustav! I\'m still learning, so if I missed your question, please reach out to info@amerikalinjen.com'
      });
    } else if (widgetType === 'strawberry') {
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 420,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#fc5e58', // Brand Coral
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#fef7f7', // Primary 50
        botColor: '#403d3c', // Neutral 900
        botAvatarBackgroundColor: '#fc5e58', // Brand Coral
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#ffd7d7', // Secondary 50
        userColor: '#403d3c', // Neutral 900
        
        // Send button colors
        sendButtonBackgroundColor: '#fc5e58', // Brand Coral
        sendButtonColor: '#FFFFFF',
        
        // Input area colors
        inputAreaBackgroundColor: '#fafafa', // Neutral 50
        
        // Toggle button colors
        toggleBackgroundColor: '#fc5e58', // Brand Coral
        toggleBackgroundColorActive: '#e53e3a', // Primary 600
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Strawberry',
        botMessage: 'Hi! How can I help you today?',
        inputPlaceholder: 'Write your message...',
        sendButtonText: 'Send',
        emailInfoText: 'Need help? Contact us at support@strawberry.com'
      });
    } else {
      // Default to That's Up values
      setStyles({
        // Chat widget colors
        chatBackgroundColor: '#FFFFFF',
        chatBorderColor: '#ccc',
        chatBorderRadius: 0,
        chatWidth: 375,
        chatHeight: 650,
        
        // Header colors
        headerBackgroundColor: '#1e3a34',
        headerColor: '#FFFFFF',
        
        // Bot message colors
        botBackgroundColor: '#1e3a34',
        botColor: '#FFFFFF',
        botAvatarBackgroundColor: '#1e3a34',
        botAvatarColor: '#FFFFFF',
        botAvatarSize: 40,
        
        // User message colors
        userBackgroundColor: '#f0f0f0',
        userColor: '#000000',
        
        // Send button colors
        sendButtonBackgroundColor: '#1e3a34',
        sendButtonColor: '#FFFFFF',
        
        // Toggle button colors
        toggleBackgroundColor: '#1e3a34',
        toggleBackgroundColorActive: '#0F1D2B',
        toggleBorderColor: '#FFFFFF',
        toggleBorderColorActive: '#FFFFFF',
        toggleSize: 60,
        toggleBorderRadius: 50,
        
        // Positioning
        position: 'bottom-right',
        bottom: 0,
        right: 0,
        left: 0,
        top: 0,
      });
      
      setTextContent({
        headerTitle: 'Vivi',
        botMessage: 'Hi! How can I help you?',
        inputPlaceholder: 'Write...',
        sendButtonText: 'Send',
        emailInfoText: 'Send an e-mail to info@villacph.com if you don\'t get an answer to your question.'
      });
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {!widgetType ? (
          // Supplier Selection Screen
          <div className="max-w-6xl">
                          <div className="text-center mb-16">
                <h2 className="text-4xl font-display font-bold text-neutral-900 mb-6 pt-16">Select Your Current Web Supplier</h2>
                <p className="text-xl text-neutral-600 font-text max-w-3xl mx-auto leading-relaxed">
                  Choose your current supplier to see an accurate representation of how your chat widget can be customized. Each supplier has unique styling and features.
                </p>
              </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {/* That's Up */}
              <div 
                className="group cursor-pointer transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out"
                onClick={() => selectWidgetType('thatsup')}
              >
                <Card className="relative bg-white/40 backdrop-blur-xl border border-white/40 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group-hover:border-blue-200/60 group-hover:bg-white/60">
                  {/* Subtle background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 via-transparent to-indigo-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Subtle corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardContent className="p-8 text-center relative z-10">
                    <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-4 group-hover:from-blue-100 group-hover:to-indigo-200 transition-all duration-300 shadow-md group-hover:shadow-lg">
                      <img src={thatsupLogo} alt="That's Up Logo" className="w-full h-full brightness-0 group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-neutral-900 mb-4 group-hover:text-blue-700 transition-colors duration-300">That's Up</h3>
                    
                    {/* Selection indicator */}
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm">
                        <span className="mr-1.5">✓</span>
                        Select
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Adeprimo */}
              <div 
                className="group cursor-pointer transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out"
                onClick={() => selectWidgetType('adeprimo')}
              >
                <Card className="relative bg-white/40 backdrop-blur-xl border border-white/40 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group-hover:border-green-200/60 group-hover:bg-white/60">
                  {/* Subtle background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/3 via-transparent to-emerald-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Subtle corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-green-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardContent className="p-8 text-center relative z-10">
                    <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-4 group-hover:from-green-100 group-hover:to-emerald-200 transition-all duration-300 shadow-md group-hover:shadow-lg">
                      <img src={adeprimoLogo} alt="Adeprimo Logo" className="w-full h-auto max-h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-neutral-900 mb-4 group-hover:text-green-700 transition-colors duration-300">Adeprimo</h3>
                    
                    {/* Selection indicator */}
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-full shadow-sm">
                        <span className="mr-1.5">✓</span>
                        Select
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Strawberry */}
              <div 
                className="group cursor-pointer transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out"
                onClick={() => selectWidgetType('strawberry')}
              >
                <Card className="relative bg-white/40 backdrop-blur-xl border border-white/40 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group-hover:border-pink-200/60 group-hover:bg-white/60">
                  {/* Subtle background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/3 via-transparent to-rose-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Subtle corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-pink-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardContent className="p-8 text-center relative z-10">
                    <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-pink-50 to-rose-100 rounded-2xl p-4 group-hover:from-pink-100 group-hover:to-rose-200 transition-all duration-300 shadow-md group-hover:shadow-lg">
                      <img src={strawberryLogo} alt="Strawberry Logo" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-neutral-900 mb-4 group-hover:text-pink-700 transition-colors duration-300">Strawberry</h3>
                    
                    {/* Selection indicator */}
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="inline-flex items-center px-3 py-1.5 bg-pink-600 text-white text-xs font-medium rounded-full shadow-sm">
                        <span className="mr-1.5">✓</span>
                        Select
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        ) : (
          // Customization Screen
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-display font-bold text-neutral-900 mb-4">Widget Customizer</h1>
              <p className="text-lg text-neutral-600 font-text">
                Customize your chat widget appearance and behavior to match your brand.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Preview Panel */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-display font-bold text-neutral-900">Live Preview</h2>
                  <p className="text-sm text-neutral-600 mt-1">Widget: {widgetType.charAt(0).toUpperCase() + widgetType.slice(1)}</p>
                </div>
                <Button onClick={resetToDefault} variant="outline" size="sm">
                  Reset to Default
                </Button>
              </div>
              
              <Card className="bg-white/30 backdrop-blur-xl border-neutral-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex justify-center items-center">
                    {/* Widget Preview */}
                    {widgetType === 'thatsup' && (
                      <ThatsUpWidget styles={styles} textContent={textContent} />
                    )}
                    {widgetType === 'adeprimo' && (
                      <AdeprimoWidget styles={styles} textContent={textContent} />
                    )}
                    {widgetType === 'strawberry' && (
                      <StrawberryWidget styles={styles} textContent={textContent} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Customization Panel */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-display font-bold text-neutral-900">Customization Options</h2>
                <Button 
                  onClick={() => setWidgetType(null)} 
                  variant="outline" 
                  size="sm"
                >
                  Change Supplier
                </Button>
              </div>
              
              <Card className="bg-white/30 backdrop-blur-xl border-neutral-200 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  {/* Colors Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-neutral-900">Colors</h3>
                     
                     <div className="grid grid-cols-2 gap-4">
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Header Background</label>
                         <input
                           type="color"
                           value={styles.headerBackgroundColor}
                           onChange={(e) => updateStyle('headerBackgroundColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Header Text</label>
                         <input
                           type="color"
                           value={styles.headerColor}
                           onChange={(e) => updateStyle('headerColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Bot Message Background</label>
                         <input
                           type="color"
                           value={styles.botBackgroundColor}
                           onChange={(e) => updateStyle('botBackgroundColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Bot Message Text</label>
                         <input
                           type="color"
                           value={styles.botColor}
                           onChange={(e) => updateStyle('botColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">User Message Background</label>
                         <input
                           type="color"
                           value={styles.userBackgroundColor}
                           onChange={(e) => updateStyle('userBackgroundColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">User Message Text</label>
                         <input
                           type="color"
                           value={styles.userColor}
                           onChange={(e) => updateStyle('userColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Bot Avatar Background</label>
                         <input
                           type="color"
                           value={styles.botAvatarBackgroundColor}
                           onChange={(e) => updateStyle('botAvatarBackgroundColor', e.target.value)}
                           className="w-full h-10 rounded-lg border border-gray-200"
                         />
                       </div>
                       
                       {widgetType !== 'adeprimo' && widgetType !== 'strawberry' && (
                         <div>
                           <label className="block text-sm font-medium text-neutral-700 mb-2">Send Button Background</label>
                           <input
                             type="color"
                             value={styles.sendButtonBackgroundColor}
                             onChange={(e) => updateStyle('sendButtonBackgroundColor', e.target.value)}
                             className="w-full h-10 rounded-lg border border-gray-200"
                           />
                         </div>
                       )}
                       
                       {widgetType !== 'strawberry' && (
                         <div>
                           <label className="block text-sm font-medium text-neutral-700 mb-2">Send Button Color</label>
                           <input
                             type="color"
                             value={styles.sendButtonColor ?? '#777777'}
                             onChange={(e) => updateStyle('sendButtonColor', e.target.value)}
                             className="w-full h-10 rounded-lg border border-gray-200"
                           />
                         </div>
                       )}
                       
                       {widgetType === 'adeprimo' && (
                         <div>
                           <label className="block text-sm font-medium text-neutral-700 mb-2">Input Area Background</label>
                           <input
                             type="color"
                             value={styles.inputAreaBackgroundColor ?? '#f8f9fa'}
                             onChange={(e) => updateStyle('inputAreaBackgroundColor', e.target.value)}
                             className="w-full h-10 rounded-lg border border-gray-200"
                           />
                         </div>
                       )}
                     </div>
                   </div>

                   {/* Text Section */}
                   <div className="space-y-4">
                     <h3 className="text-lg font-semibold text-neutral-900">Text</h3>
                     
                     <div>
                       <label className="block text-sm font-medium text-neutral-700 mb-2">Header Title</label>
                       <input
                         type="text"
                         value={textContent.headerTitle}
                         onChange={(e) => updateText('headerTitle', e.target.value)}
                         className="w-full p-2 border border-gray-200 rounded-lg"
                         placeholder="Enter header title"
                       />
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-neutral-700 mb-2">Bot Message</label>
                       <input
                         type="text"
                         value={textContent.botMessage}
                         onChange={(e) => updateText('botMessage', e.target.value)}
                         className="w-full p-2 border border-gray-200 rounded-lg"
                         placeholder="Enter bot message"
                       />
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-neutral-700 mb-2">Input Placeholder</label>
                       <input
                         type="text"
                         value={textContent.inputPlaceholder}
                         onChange={(e) => updateText('inputPlaceholder', e.target.value)}
                         className="w-full p-2 border border-gray-200 rounded-lg"
                         placeholder="Enter input placeholder"
                       />
                     </div>
                     
                     {widgetType !== 'adeprimo' && widgetType !== 'strawberry' && (
                       <div>
                         <label className="block text-sm font-medium text-neutral-700 mb-2">Send Button Text</label>
                         <input
                           type="text"
                           value={textContent.sendButtonText}
                           onChange={(e) => updateText('sendButtonText', e.target.value)}
                           className="w-full p-2 border border-gray-200 rounded-lg"
                           placeholder="Enter button text"
                         />
                       </div>
                     )}
                     
                     <div>
                       <label className="block text-sm font-medium text-neutral-700 mb-2">Email Info Text</label>
                       <textarea
                         value={textContent.emailInfoText}
                         onChange={(e) => updateText('emailInfoText', e.target.value)}
                         className="w-full p-2 border border-gray-200 rounded-lg"
                         rows={3}
                         placeholder="Enter email info text"
                       />
                     </div>
                   </div>
                 </CardContent>
               </Card>
             </div>
           </div>
         </>
         )}
       </div>
     </div>
   );
}
